import { DealerState } from '@appTypes/DealerState';
import NotesIcon from '@components/shared/react-table-wrapper/notes-icon/NotesIcon';
import { Typography } from '@components/style';
import { useSearchDeals } from '@hooks/header/UseSearchDeals';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { AppState } from '@store/AppState';
import { theme } from '@theme';
import { truncate } from '@utils/HelperFunctions';
import React, { FC, useEffect, useMemo } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import PrivateIncentiveIcon from '../../private-incentive-icon/PrivateIncentiveIcon';
import InsightsIcon from './insights/InsightsIcon';

export type NotesAndInsightsProps = {
  dealerId: string;
  inMarket: boolean;
  isConquest: boolean;
  isFordPrivateIncentive?: boolean; //TEMPORARY OPTIONAL
  isPrivateIncentiveApplied: boolean;
  hasOpenRecall?: boolean;
  isBalloon: boolean;
  recall?: Array<string>;
  wasMailerSent: boolean;
  closedDealId: string;
  fullName: string;
  vin: string;
  status: string;
  subStatus: string;
  isLeaseBuyout: boolean;
  notesCount?: number;
  handleOpenNotesModal: (e: React.MouseEvent<HTMLSpanElement>) => void;
  expireDate?: string;
  privateIncentiveAmount?: string | null;
  isEvEnabled?: boolean;
  handleOpenPrivateIncentiveModal?: (e: React.MouseEvent) => void;
  type?: string;
  isConquestPrivateIncentiveApplied?: boolean;
  isCpo?: boolean;
  shopperActivity?: Array<string>;
};

const NotesAndInsights: FC<NotesAndInsightsProps> = (props) => {
  const { isDealerOptIn } = useSearchDeals();
  const { storeServices, featureToggle } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );
  const { isMobileDevice } = useCheckDevice();
  const isFordPrivateIncentive = useMemo(
    () => featureToggle?.fordPrivateIncentive && props?.isFordPrivateIncentive,
    [props?.isFordPrivateIncentive, featureToggle?.fordPrivateIncentive],
  );

  const getRecallTooltip = (): JSX.Element => {
    return (
      <div className='recall-tooltip-wrapper'>
        <Typography
          color={theme.palette.white}
          size={theme.typography.h6}
          fontWeight={theme.typography.fontWeights.bold}
        >
          NHTSA Recall Info
        </Typography>
        <ul>
          {props.recall?.slice(0, 2).map((recallData, index) => (
            <li key={index}>
              <h6>{truncate(recallData.split(' ^ ')[0], 60)}</h6>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  useEffect(() => {
    if (Modal.defaultStyles.overlay) {
      Modal.defaultStyles.overlay.backgroundColor = `rgb(0,0,0,.4)`;
      Modal.defaultStyles.overlay.zIndex = 205;
      Modal.defaultStyles.overlay.overflowY = `auto`;
    }
    Modal.setAppElement('#modalHandler');
  }, []);

  const privateIncentiveTooltip = (): string => {
    const primaryIncentiveAmount =
      props.privateIncentiveAmount !== '' && props.privateIncentiveAmount !== null
        ? parseInt(props.privateIncentiveAmount)
        : 0;
    const msg = primaryIncentiveAmount > 0 ? 'Click for more details' : 'Private Incentive';
    return msg;
  };

  const generatePrivateIncentiveIcon = () => {
    if (props.type === 'service-to-sales') {
      if (props.isConquestPrivateIncentiveApplied) {
        return (
          isDealerOptIn(props.dealerId) && (
            <PrivateIncentiveIcon
              toolTipText={!isMobileDevice && privateIncentiveTooltip()}
              click={props.handleOpenPrivateIncentiveModal}
              size={30}
            />
          )
        );
      } else if (props.isPrivateIncentiveApplied) {
        return (
          <PrivateIncentiveIcon
            toolTipText={!isMobileDevice && privateIncentiveTooltip()}
            click={props.handleOpenPrivateIncentiveModal}
            size={30}
          />
        );
      }
    } else if (props.isPrivateIncentiveApplied) {
      if (props.isConquest) {
        return (
          isDealerOptIn(props.dealerId) && (
            <PrivateIncentiveIcon
              toolTipText={!isMobileDevice && privateIncentiveTooltip()}
              click={props.handleOpenPrivateIncentiveModal}
              size={30}
            />
          )
        );
      } else {
        return (
          <PrivateIncentiveIcon
            toolTipText={!isMobileDevice && privateIncentiveTooltip()}
            click={props.handleOpenPrivateIncentiveModal}
            size={30}
          />
        );
      }
    }
  };

  const getShopperActivityTooltip = (): JSX.Element => {
    return (
      <div className='shopper-activity-tooltip-wrapper'>
        {props.shopperActivity?.map((shopperActivity, index) => (
          <ul key={index}>
            <span>{shopperActivityLabels[shopperActivity || shopperActivity]}</span>
          </ul>
        ))}
      </div>
    );
  };

  const shopperActivityLabels: Record<string, string> = {
    websiteVisited: 'Website Visited',
    vehicleViewed: 'Vehicle Viewed',
    leadSubmitted: 'Lead Submitted',
  };
  return (
    <Wrapper
      className='portfolio-notes-and-insights'
      data-testid='notes-and-insights'
      id='modalHandler'
    >
      {props.closedDealId && (
        <>
          <NotesIcon notesCount={props.notesCount} size={27} click={props.handleOpenNotesModal} />
          {isMobileDevice && (
            <NotesTextWrapper onClick={props.handleOpenNotesModal}>Notes</NotesTextWrapper>
          )}
        </>
      )}
      {props.inMarket && (
        <InsightsIcon
          toolTipText={!isMobileDevice && <>Household In-Market</>}
          type='in-market'
          size={30}
        />
      )}
      {props.isConquest && (
        <InsightsIcon
          toolTipText={!isMobileDevice && <>Service-to-Sales</>}
          type='conquest'
          size={30}
        />
      )}
      {props.isBalloon && (
        <InsightsIcon
          toolTipText={!isMobileDevice && <>Balloon Payment</>}
          type='balloon'
          size={30}
        />
      )}
      {generatePrivateIncentiveIcon()}
      {props.isLeaseBuyout && (
        <InsightsIcon
          toolTipText={!isMobileDevice && <>Lease Buyout</>}
          type='lease-buyout'
          size={30}
        />
      )}
      {props.wasMailerSent && (
        <InsightsIcon
          toolTipText={!isMobileDevice && <>Campaign Received</>}
          type='campaign-open'
          size={29}
        />
      )}
      {props.recall &&
        props.recall?.length > 0 &&
        props.hasOpenRecall &&
        storeServices.hasRecall &&
        featureToggle.recall && (
          <InsightsIcon
            additionalCssClass='recall-tooltip'
            toolTipText={!isMobileDevice && getRecallTooltip()}
            type='recall'
            size={28}
            color={theme.palette.brightOrange}
          />
        )}
      {props.isEvEnabled && (
        <InsightsIcon toolTipText={!isMobileDevice && <>EV</>} type='ev-leaf' size={30} />
      )}
      {props.isCpo && (
        <InsightsIcon
          toolTipText={!isMobileDevice && <>Certified Pre-owned</>}
          type='certified-preowned'
          size={30}
        />
      )}
      {isFordPrivateIncentive && (
        <InsightsIcon
          dataTestId='fordPrivateIncentiveIcon'
          toolTipText={!isMobileDevice && <>FMC Private Offer</>}
          type='incentives-alt'
          size={30}
        />
      )}
      {props.shopperActivity &&
        props.shopperActivity.length > 0 &&
        featureToggle?.websiteShopperActivity && (
          <InsightsIcon
            toolTipText={!isMobileDevice && getShopperActivityTooltip()}
            type='visit'
            size={30}
          />
        )}
    </Wrapper>
  );
};

export default NotesAndInsights;

const Wrapper = styled.span`
  display: flex;
  flex-wrap: wrap;

  & > * {
    margin: 0 10px 0 0;
  }
  .shopper-activity-tooltip-wrapper {
    ul {
      text-align: center;
      padding: 6px 0px 0px 0px;
      margin-bottom: 4px;
    }
  }
`;

const NotesTextWrapper = styled.span`
  font-size: large;
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.palette.blue};
`;
